import { createReducer, type Draft } from '@reduxjs/toolkit';
import merge from 'lodash.mergewith';

import Utils from '../../../../utils/utils';
import * as ActionTypes from '../../actions';
import { ActionManagerInstance } from '../../../../actions/manager';

// Move initial state here to avoid circular dependency
const initialState = {};

const removeChildContainers = (draft: Draft<Record<string, unknown>>, childContainers: any[] = []) => {
  childContainers.forEach((element) => {
    Utils.deleteIn(draft, [element]);
  });
};

const removeContainerKey = (
  key: string,
  draft: Draft<Record<string, unknown>>,
  target: string,
  containerItemID: string
) => {
  if (key === '') {
    Utils.deleteIn(draft, [target, 'items', containerItemID, 'key']);
  }
};

const mergePayloadInContainerItem = (draft: Draft<Record<string, unknown>>, action: { type: string; payload: any }) => {
  const { type, payload } = action;
  const isUpdate = type === ActionTypes.UPDATE_CONTAINER_ITEM;
  const {
    target,
    context,
    semanticURL = '',
    root,
    containerItemID,
    childContainers,
    flowName,
    resourceType = '',
    key = '',
    clearView = false
  } = payload;
  if (!root) {
    if (clearView) {
      Utils.deleteIn(draft, [target, 'items', containerItemID, 'view']);
    }
  } else {
    Utils.setIn(draft, [target, 'items', containerItemID, 'view'], root);
  }
  if (context) Utils.setIn(draft, [target, 'items', containerItemID, 'context'], context);
  if (flowName) Utils.setIn(draft, [target, 'items', containerItemID, 'flowName'], flowName);

  /* for update container item action, key can be optional (so will have previous value if key is not received in payload */
  if (!isUpdate || (isUpdate && key)) {
    Utils.setIn(draft, [target, 'items', containerItemID, 'key'], key.trim());
  }

  removeContainerKey(key, draft, target, containerItemID);

  /* for update container item action, semanticURL can be optional (so will have previous value if semanticURL is not received in payload */
  if (!isUpdate || (isUpdate && semanticURL)) {
    Utils.setIn(draft, [target, 'items', containerItemID, 'semanticURL'], semanticURL);
  }

  /* for update container item action, resourceType can be optional (so will have previous value if resourceType is not received in payload */
  if (!isUpdate || (isUpdate && resourceType)) {
    Utils.setIn(draft, [target, 'items', containerItemID, 'resourceType'], resourceType);
  }

  removeChildContainers(draft, childContainers);
  return draft;
};

const replaceContainerItem = (draft: Draft<Record<string, any>>, action: any) => {
  const { actionMgrID, data, target, containerItemID } = action.payload;
  let previousDraft = draft;
  try {
    const type = draft && draft[target]?.type;
    let prevAccessedOrder = (draft && draft[target]?.accessedOrder) || [];
    if (type === 'single') {
      prevAccessedOrder = prevAccessedOrder.length > 0 ? prevAccessedOrder : [...prevAccessedOrder, containerItemID];
    } else {
      prevAccessedOrder = prevAccessedOrder.includes(containerItemID)
        ? prevAccessedOrder
        : [...prevAccessedOrder, containerItemID];
    }
    Utils.setIn(draft, [target, 'items', containerItemID, 'transientItems'], []);
    Utils.setIn(draft, [target, 'accessedOrder'], prevAccessedOrder);
    previousDraft = mergePayloadInContainerItem(draft, action);
    ActionManagerInstance.resolveActionByID(actionMgrID, data, false);
  } catch {
    ActionManagerInstance.rejectActionByID(actionMgrID, false);
  }

  return previousDraft;
};

const routeReducer = createReducer(initialState, (builder) => {
  builder
    .addCase(ActionTypes.RESET_STORE, () => {
      return initialState;
    })
    .addCase(ActionTypes.INIT_CONTAINERS, (state: any, action: any) => {
      const { context, name, type, actionMgrID } = action.payload;
      const target = `${context}/${name}`;

      ActionManagerInstance.resolveActionByID(actionMgrID, null, false);
      if (state[target]) {
        return state;
      }

      Utils.setIn(state, [target], {
        type,
        accessedOrder: [],
        items: {}
      });
    })
    .addCase(ActionTypes.REMOVE_CONTAINER, (state, action: any) => {
      const { containerName, childContainers = [] } = action.payload;
      Utils.deleteIn(state, [containerName]);
      childContainers.forEach((container: any) => {
        Utils.deleteIn(state, [container]);
      });
    })
    .addCase(ActionTypes.RESET_CONTAINERS, (state, action: any) => {
      const { context, name, actionMgrID, childContainers } = action.payload;
      const target = `${context}/${name}`;

      ActionManagerInstance.resolveActionByID(actionMgrID, null, false);

      Utils.setIn(state, [target, 'accessedOrder'], []);
      Utils.setIn(state, [target, 'items'], {});
      if (Array.isArray(childContainers)) {
        childContainers.forEach((container) => {
          Utils.deleteIn(state, [container]);
        });
      }
    })
    .addCase(ActionTypes.ADD_CONTAINER_ITEM, (state: any, action: any) => {
      const {
        target,
        context,
        isInCreateStage,
        isBulkAction,
        semanticURL,
        root,
        containerItemID,
        actionMgrID,
        key,
        flowName,
        resourceType,
        actionName,
        isMinimizable,
        showProgress,
        progressMessage,
        modalOptions,
        resourceStatus,
        actionID,
        isMultiRecordData,
        acTertiary,
        parentContext,
        heading,
        isTraditionalRemoteCase,
        readOnly,
        referenceList,
        subscriptName,
        subscriptFieldName,
        pageGroupName,
        isPageGroup,
        index,
        isDeleteConfirmation,
        workId,
        caseTypeID,
        className,
        keys,
        enableBackNavigation
      } = action.payload;

      const prevContainerState = state[target];
      const type = prevContainerState && prevContainerState.type;
      const prevAccessedOrder = (prevContainerState && prevContainerState.accessedOrder) || [];
      const prevItems = (prevContainerState && prevContainerState.items) || {};

      if (state[target] && type === 'single' && prevAccessedOrder.length > 0) {
        return mergePayloadInContainerItem(state, action);
      }

      const addContainerObject = {
        accessedOrder: [...prevAccessedOrder, containerItemID],
        items: {
          ...prevItems,
          [containerItemID]: {
            view: root,
            semanticURL,
            key,
            flowName,
            readOnly,
            context,
            isInCreateStage,
            resourceType,
            actionName,
            isMinimizable,
            ...(isBulkAction && { isBulkAction }),
            showProgress,
            progressMessage,
            modalOptions,
            isTraditionalRemoteCase,
            resourceStatus,
            referenceList,
            subscriptName,
            subscriptFieldName,
            pageGroupName,
            isPageGroup,
            index,
            ...(actionID && { actionID }),
            ...(acTertiary && { acTertiary }),
            ...(isMultiRecordData && { isMultiRecordData }),
            ...(parentContext && { parentContext }),
            ...(isDeleteConfirmation && {
              isDeleteConfirmation,
              workId,
              caseTypeID,
              className,
              keys,
              enableBackNavigation
            }),
            heading
          }
        }
      };

      ActionManagerInstance.resolveActionByID(actionMgrID, null, false);
      state[target] = merge(state[target], addContainerObject);
    })
    .addCase(ActionTypes.UPDATE_CONTAINER_ITEM, (state, action: any) => {
      const { actionMgrID, data } = action.payload;
      try {
        mergePayloadInContainerItem(state, action);
        ActionManagerInstance.resolveActionByID(actionMgrID, data, false);
      } catch {
        ActionManagerInstance.rejectActionByID(actionMgrID, false);
      }
    })
    .addCase(ActionTypes.REPLACE_CONTAINER_ITEM, (state, action) => {
      return replaceContainerItem(state, action);
    })
    .addCase(ActionTypes.ACTIVATE_CONTAINER_ITEM, (state, action: any) => {
      const { target, containerItemID, actionMgrID } = action.payload;
      try {
        const filteredAccessedOrder = Utils.getIn(state, [target, 'accessedOrder']).filter(
          (item: any) => item !== containerItemID
        );
        filteredAccessedOrder.push(containerItemID);
        Utils.setIn(state, [target, 'accessedOrder'], filteredAccessedOrder);
        ActionManagerInstance.resolveActionByID(actionMgrID, null, false);
      } catch {
        ActionManagerInstance.rejectActionByID(actionMgrID, false);
      }
    })
    .addCase(ActionTypes.UPMERGE_AND_REMOVE_CONTAINER_ITEM, (state, action: any) => {
      const { target, containerItemID, actionMgrID, childContainers } = action.payload;
      try {
        const newAccessedOrder = Utils.getIn(state, [target, 'accessedOrder']).filter(
          (item: any) => item !== containerItemID
        );
        Utils.setIn(state, [target, 'accessedOrder'], newAccessedOrder);
        Utils.deleteIn(state, [target, 'items', containerItemID]);
        removeChildContainers(state, childContainers);
        ActionManagerInstance.resolveActionByID(actionMgrID, null, false);
      } catch {
        ActionManagerInstance.rejectActionByID(actionMgrID, false);
      }
    })
    .addCase(ActionTypes.REMOVE_CONTAINER_ITEM, (state, action: any) => {
      const { target, containerItemID, actionMgrID, childContainers } = action.payload;
      try {
        const newAccessedOrder = Utils.getIn(state, [target, 'accessedOrder']).filter(
          (item: any) => item !== containerItemID
        );
        Utils.setIn(state, [target, 'accessedOrder'], newAccessedOrder);
        Utils.deleteIn(state, [target, 'items', containerItemID]);
        removeChildContainers(state, childContainers);
        ActionManagerInstance.resolveActionByID(actionMgrID, null, false);
      } catch {
        ActionManagerInstance.rejectActionByID(actionMgrID, false);
      }
    })
    .addCase(ActionTypes.ADD_TRANSIENT_ITEM, (state, action: any) => {
      const { transientItemID, target, contextName } = action.payload;
      const transientItems = Utils.getIn(state, [target, 'items', contextName, 'transientItems']) ?? [];
      Utils.setIn(state, [target, 'items', contextName, 'transientItems'], [...transientItems, transientItemID]);
    })
    .addCase(ActionTypes.REMOVE_ALL_TRANSIENT_ITEMS, (state, action: any) => {
      const { target, containerItemID } = action.payload;
      Utils.deleteIn(state, [target, 'items', containerItemID, 'transientItems']);
    })
    .addCase(ActionTypes.REMOVE_TRANSIENT_ITEM, (state, action: any) => {
      const { target, transientItemID, containerItemID } = action.payload;
      const transientItems = Utils.getIn(state, [target, 'items', containerItemID, 'transientItems']) ?? [];
      const filteredTransientItems = transientItems.filter((item: any) => item !== transientItemID);

      Utils.setIn(state, [target, 'items', containerItemID, 'transientItems'], filteredTransientItems);
    });
});

export default routeReducer;
